import React from "react";
import { Descriptions, Image, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatDay, formatEuro, formatMinutes, formatQuantity } from "./format";

/**
 * What the customer reads before signing (and sees afterwards): the work, the times, the
 * materials and, when shown, the amounts. `view` has the shape of the server's customerView.
 */
const ReportPreview = ({ view, photoSrc, compact = false }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  if (!view) return null;
  const prices = Boolean(view.showPrices);
  const totals = view.totals || {};

  return (
    <div className="wr-preview">
      <div className="wr-preview-head">
        <div>
          <Typography.Title level={4} className="wr-preview-title">
            {t("Work report")} {view.number}
          </Typography.Title>
          <Typography.Text type="secondary">
            {[formatDay(view.date), view.seller?.name].filter(Boolean).join(" · ")}
          </Typography.Text>
        </div>
      </div>

      <Descriptions size="small" column={1} className="wr-preview-facts">
        {view.customer?.name && (
          <Descriptions.Item label={t("Customer")}>{view.customer.name}</Descriptions.Item>
        )}
        {view.project?.name && (
          <Descriptions.Item label={t("Project")}>{view.project.name}</Descriptions.Item>
        )}
        {view.siteAddress && (
          <Descriptions.Item label={t("Site")}>{view.siteAddress}</Descriptions.Item>
        )}
        {view.orderReference && (
          <Descriptions.Item label={t("Order")}>{view.orderReference}</Descriptions.Item>
        )}
      </Descriptions>

      <Typography.Title level={5}>{t("Work carried out")}</Typography.Title>
      <Typography.Paragraph className="wr-preview-text">
        {view.workDescription || "—"}
      </Typography.Paragraph>

      {view.labour?.length > 0 && (
        <>
          <Typography.Title level={5}>{t("Working times")}</Typography.Title>
          <ul className="wr-preview-lines">
            {view.labour.map((line, index) => (
              <li key={line._id || index}>
                <div className="wr-preview-line-main">
                  <span>
                    <strong>{line.name || "—"}</strong>{" "}
                    <Tag bordered={false}>{line.kind === "TRAVEL" ? t("Travel") : t("Work")}</Tag>
                  </span>
                  <span className="wr-num">{formatMinutes(line.minutes)} h</span>
                </div>
                <div className="wr-preview-line-sub">
                  <span>
                    {[
                      line.start && line.end ? `${line.start}–${line.end}` : "",
                      line.breakMinutes ? `${t("Break")} ${formatMinutes(line.breakMinutes)}` : "",
                      view.rounding && line.billedMinutes !== line.minutes
                        ? `${t("billed")} ${formatMinutes(line.billedMinutes)}`
                        : "",
                      line.note
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  {prices && line.net !== undefined && (
                    <span className="wr-num">
                      {formatEuro(line.rate, lng)}/h · {formatEuro(line.net, lng)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <Typography.Text type="secondary">
            {t("Working time")} {formatMinutes(totals.workMinutes)} h
            {totals.travelMinutes
              ? ` · ${t("Travel time")} ${formatMinutes(totals.travelMinutes)} h`
              : ""}
            {view.rounding
              ? ` · ${t("Billed per started {{n}} minutes", { n: view.rounding })}`
              : ""}
          </Typography.Text>
        </>
      )}

      {view.materials?.length > 0 && (
        <>
          <Typography.Title level={5}>{t("Materials and flat rates")}</Typography.Title>
          <ul className="wr-preview-lines">
            {view.materials.map((line, index) => (
              <li key={line._id || index}>
                <div className="wr-preview-line-main">
                  <span>
                    {line.description || "—"}
                    {line.articleNumber ? (
                      <Typography.Text type="secondary"> ({line.articleNumber})</Typography.Text>
                    ) : null}
                  </span>
                  <span className="wr-num">
                    {formatQuantity(line.quantity, lng)} {line.unit}
                  </span>
                </div>
                {prices && line.net !== undefined && (
                  <div className="wr-preview-line-sub">
                    <span />
                    <span className="wr-num">
                      {formatEuro(line.unitPrice, lng)} · {formatEuro(line.net, lng)}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {prices && totals.net !== undefined ? (
        <dl className="wr-preview-totals">
          <dt>{t("Net")}</dt>
          <dd>{formatEuro(totals.net, lng)}</dd>
          <dt>
            {t("VAT")} {view.taxRate} %
          </dt>
          <dd>{formatEuro(totals.tax, lng)}</dd>
          <dt className="wr-gross">{t("Gross")}</dt>
          <dd className="wr-gross">{formatEuro(totals.gross, lng)}</dd>
        </dl>
      ) : (
        <Typography.Paragraph type="secondary">
          {t("Prices follow with the invoice.")}
        </Typography.Paragraph>
      )}

      {!compact && view.photos?.length > 0 && photoSrc && (
        <>
          <Typography.Title level={5}>{t("Photos")}</Typography.Title>
          <div className="wr-photo-grid">
            <Image.PreviewGroup>
              {view.photos.map(photo => (
                <figure key={photo._id}>
                  <Image src={photoSrc(photo._id)} alt={photo.caption || ""} />
                  {photo.caption && <figcaption>{photo.caption}</figcaption>}
                </figure>
              ))}
            </Image.PreviewGroup>
          </div>
        </>
      )}

      {view.terms && (
        <Typography.Paragraph type="secondary" className="wr-preview-terms">
          {view.terms}
        </Typography.Paragraph>
      )}

      {view.signature && (
        <div className="wr-signed">
          <img src={view.signature.image} alt={t("Signature")} />
          <div>
            <strong>{view.signature.name}</strong>
            <div>
              {t("Signed on")} {formatDateTime(view.signature.signedAt, lng)} (
              {view.signature.method === "REMOTE" ? t("signed via link") : t("signed on site")})
            </div>
          </div>
        </div>
      )}
      {view.refusal && (
        <div className="wr-refused">
          <strong>{t("Signature refused")}</strong> · {formatDateTime(view.refusal.at, lng)}
          <div>{view.refusal.reason}</div>
        </div>
      )}
    </div>
  );
};

export default ReportPreview;
